<template>
    <div
        ref="wrapper"
        class="ds-multiselect flex flex-col gap-1"
        :class="`ds-multiselect-size-${size}`"
    >
        <!-- visually hidden native select -->
        <select
            v-model="selectedOptions"
            :id="elementId"
            ref="selectElement"
            class="visually-hidden fixed left-offscreen"
            :disabled
            multiple
            :name="`ds-multiselect-${label}`"
            tabIndex="-1"
        >
            <option
                v-for="option in props.options"
                :key="option.id"
                :value="option.value"
            >
                {{ option.label }}
            </option>
        </select>

        <div class="flex w-full flex-col gap-1">
            <DsInputLabel
                :id="elementId"
                :disabled
                :label
                :labelTooltip
                :required
                :size
            >
                <template
                    v-if="$slots.labelTooltip"
                    #labelTooltip
                >
                    <slot name="labelTooltip" />
                </template>
            </DsInputLabel>

            <DsPopover
                :open="isOpen && !disabled"
                @update:open="handleOpen"
            >
                <DsPopoverTrigger>
                    <slot
                        :isOpen
                        name="trigger"
                    >
                        <div
                            ref="controlWrapper"
                            :class="[
                                'control-wrapper rounded px-3',
                                {
                                    'cursor-pointer': !disabled,
                                    'is-open': isOpen,
                                    'is-disabled': disabled,
                                    'has-error': validationState === 'error',
                                    'has-warning': validationState === 'warning',
                                },
                            ]"
                            data-testid="ds-multiselect-control"
                            :tabindex="disabled ? -1 : 0"
                        >
                            <div class="flex w-full items-center justify-between">
                                <div class="flex flex-wrap items-center gap-2">
                                    <DsTooltip
                                        v-for="opt in visibleSelectedOptions"
                                        :key="opt.id"
                                        :tooltipText="opt.tooltipText ?? ''"
                                    >
                                        <div class="flex h-6 items-center gap-2 rounded-full bg-ds-primary-200 px-2">
                                            <span
                                                class="text-content-md font-medium text-content-primary"
                                                :data-testid="`ds-multiselect-select-chip-${opt.id}`"
                                            >
                                                <slot
                                                    name="chipContent"
                                                    :option="opt"
                                                    :truncate="truncateChipLabel(opt.id)"
                                                >
                                                    {{ truncateChipLabel(opt.id)(opt.label) }}
                                                </slot>
                                            </span>
                                            <button
                                                :aria-label="`Remove ${opt.label}`"
                                                class="flex items-center"
                                                :class="{ 'cursor-pointer': !disabled }"
                                                :disabled
                                                @click.stop="handleIndividualChange(opt.id, !selectedIds.has(opt.id))"
                                            >
                                                <DsIcon
                                                    class="text-content-primary"
                                                    icon="fa-solid fa-circle-xmark"
                                                    size="sm"
                                                />
                                            </button>
                                        </div>
                                    </DsTooltip>

                                    <template v-if="!!hiddenOptionsCount && showSelectionCounterOnOverflow">
                                        <span
                                            class="text-content-md text-content-secondary"
                                            :class="{ 'cursor-pointer': !disabled }"
                                            data-testid="ds-multiselect-selection-overflow-counter"
                                            @click.stop="isOpen = true"
                                        >
                                            +{{ hiddenOptionsCount }} more
                                        </span>
                                    </template>

                                    <span
                                        v-if="!visibleSelectedOptions.length && !hiddenOptionsCount"
                                        class="ds-multiselect-placeholder"
                                    >
                                        {{ placeholder }}
                                    </span>
                                </div>
                                <slot name="trailing-icons">
                                    <div class="flex items-center">
                                        <DsIcon
                                            v-if="isOpen"
                                            class="text-content-primary"
                                            icon="fa-solid fa-chevron-up"
                                            size="sm"
                                        />
                                        <DsIcon
                                            v-else
                                            class="text-content-primary"
                                            icon="fa-solid fa-chevron-down"
                                            size="sm"
                                        />
                                    </div>
                                </slot>
                            </div>
                        </div>
                    </slot>
                </DsPopoverTrigger>

                <DsPopoverContent
                    align="end"
                    side="bottom"
                    :sideOffset="8"
                >
                    <div
                        class="max-h-[400px] overflow-y-auto"
                        :class="[{ 'w-[var(--reka-popover-trigger-width)]': isFullWidth }, { 'text-content-md': size === 'sm' }]"
                    >
                        <div class="flex flex-col gap-2 border-b border-ds-primary-200 p-2">
                            <DsSearchInput
                                v-if="hasSearchInput"
                                v-model="searchQuery"
                                autofocus
                                name="ds-multiselect-search"
                                :placeholder="searchInputPlaceholder ?? `Search ${label?.toLowerCase()}`"
                                :size
                            />

                            <div
                                v-if="!filteredOptions.length"
                                class="p-4"
                            >
                                <slot name="emptyState">
                                    <DsSelectEmptyState
                                        icon="fa-solid fa-magnifying-glass"
                                        subtitle="Adjust your search and try again"
                                        title="No results found"
                                    >
                                        <DsButton
                                            size="sm"
                                            variant="tertiary"
                                            @click="searchQuery = ''"
                                        >
                                            Clear search
                                        </DsButton>
                                    </DsSelectEmptyState>
                                </slot>
                            </div>

                            <DsMultiSelectOption
                                v-if="showSelectAll && !!props.options.length && (!hasSearchQuery || !!filteredOptions.length)"
                                :disabled="!totalOptions || disableSelectAll"
                                :isSelected="selectAllModel"
                            >
                                <template #leading>
                                    <DsCheckbox
                                        v-model="selectAllModel"
                                        :id="`${elementId}-select-all`"
                                        data-testid="ds-multiselect-select-all"
                                        :disabled="!totalOptions || disableSelectAll"
                                        :icon="selectAllState === 'indeterminate' ? 'fa-solid fa-minus' : 'fa-solid fa-check'"
                                        label="Select all"
                                        :name="`${elementId}-select-all`"
                                        value="'select-all'"
                                    />
                                </template>
                            </DsMultiSelectOption>
                        </div>

                        <div v-if="!!filteredOptions.length">
                            <ul class="options">
                                <template v-if="isGrouped">
                                    <DsMultiSelectGroupWrapper
                                        v-for="(group, index) in filteredOptions"
                                        :key="group.label + '-' + index"
                                        :data-testid="`ds-multiselect-group-${group.label}`"
                                        :items="group.items"
                                        :label="group.label ?? ''"
                                        :selectedOptions
                                        @groupSelectAll="handleGroupSelectAll(group.items, $event)"
                                    >
                                        <li
                                            v-for="opt in group.items"
                                            :key="opt.id"
                                            class="flex items-center justify-between"
                                        >
                                            <DsMultiSelectOption
                                                :disabled="opt.disabled"
                                                :isSelected="selectedIds.has(opt.id)"
                                            >
                                                <template #leading>
                                                    <DsCheckbox
                                                        :id="opt.id"
                                                        :disabled="opt.disabled"
                                                        :model-value="selectedIds.has(opt.id)"
                                                        :name="opt.label"
                                                        :value="opt.id.toString()"
                                                        @update:model-value="checked => handleIndividualChange(opt.id, checked)"
                                                    >
                                                        <template #label>
                                                            <slot
                                                                :isSelected="selectedIds.has(opt.id)"
                                                                name="optionLabel"
                                                                :option="opt"
                                                            >
                                                                <span :data-testid="`ds-multiselect-option-${opt.id}`">
                                                                    {{ opt.label }}
                                                                </span>
                                                            </slot>
                                                        </template>
                                                    </DsCheckbox>
                                                </template>
                                            </DsMultiSelectOption>
                                        </li>
                                    </DsMultiSelectGroupWrapper>
                                </template>
                                <template v-else>
                                    <div class="p-2">
                                        <li
                                            v-for="opt in filteredOptions[0].items"
                                            :key="opt.id"
                                            class="flex items-center justify-between"
                                        >
                                            <DsMultiSelectOption
                                                class=""
                                                :disabled="opt.disabled"
                                                :isSelected="selectedIds.has(opt.id)"
                                                @click="
                                                    event => {
                                                        if (event.target === event.currentTarget && !opt.disabled) {
                                                            handleIndividualChange(opt.id, !selectedIds.has(opt.id));
                                                        }
                                                    }
                                                "
                                            >
                                                <template #leading>
                                                    <DsCheckbox
                                                        :id="opt.id"
                                                        :disabled="opt.disabled"
                                                        :model-value="selectedIds.has(opt.id)"
                                                        :name="opt.label"
                                                        :value="opt.id.toString()"
                                                        @update:modelValue="checked => handleIndividualChange(opt.id, checked)"
                                                    >
                                                        <template #label>
                                                            <slot
                                                                :isSelected="selectedIds.has(opt.id)"
                                                                name="optionLabel"
                                                                :option="opt"
                                                            >
                                                                <span :data-testid="`ds-multiselect-option-${opt.id}`">
                                                                    {{ opt.label }}
                                                                </span>
                                                            </slot>
                                                        </template>
                                                    </DsCheckbox>
                                                </template>

                                                <template #trailing>
                                                    <slot name="optionTrailing"></slot>
                                                </template>
                                            </DsMultiSelectOption>
                                        </li>
                                    </div>
                                </template>
                            </ul>
                        </div>
                    </div>
                </DsPopoverContent>
            </DsPopover>
        </div>

        <HelperMessage
            :actionableHelperText
            :error
            :helperText
            :size
            :warning
            @clickHelperText="emit('clickHelperText')"
        />
    </div>
</template>

<script setup lang="ts" generic="T extends { id: string | number }">
import DsIcon from '@/components/design-system/DsIcon.vue';
import DsSelectEmptyState from '@/components/design-system/DsSelectEmptyState.vue';
import DsButton from '@/components/design-system/button/DsButton.vue';
import DsCheckbox from '@/components/design-system/form/input/DsCheckbox.vue';
import DsInputLabel from '@/components/design-system/form/input/DsInputLabel/DsInputLabel.vue';
import DsMultiSelectGroupWrapper from '@/components/design-system/multiselect/DsMultiSelectGroup.vue';
import DsMultiSelectOption from '@/components/design-system/multiselect/DsMultiSelectOption.vue';
import {
    SelectAllState,
    type DsMultiSelectEmits,
    type DsMultiSelectOptionBase,
    type DsMultiSelectProps,
} from '@/components/design-system/multiselect/multi-select.types';
import DsPopover from '@/components/design-system/popover/DsPopover.vue';
import DsPopoverContent from '@/components/design-system/popover/DsPopoverContent.vue';
import DsPopoverTrigger from '@/components/design-system/popover/DsPopoverTrigger.vue';
import DsTooltip from '@/components/design-system/tooltip/DsTooltip.vue';
import { createFuzzyRegex } from '@/shared/utils/fuzzy-search';
import DsSearchInput from '@ds/form/input/DsSearchInput/DsSearchInput.vue';
import HelperMessage from '@ds/utils/HelperMessage.vue';
import { onKeyDown, refDebounced } from '@vueuse/core';
import { computed, ref, useId, watch } from 'vue';

const DEFAULT_MAX_VISIBLE_CHIPS = 10;

const props = withDefaults(defineProps<DsMultiSelectProps<T>>(), {
    helperText: '',
    isFullWidth: true,
    disabled: false,
    disableSelectAll: false,
    showSelectAll: true,
    maxVisibleChips: DEFAULT_MAX_VISIBLE_CHIPS,
    /** Once more items than the maximum allowed are selected, show a counter */
    showSelectionCounterOnOverflow: false,
    size: 'md',
    warning: false,
    actionableHelperText: false,
});

const selectedOptions = defineModel<T[]>({ default: () => [] });
const searchQuery = defineModel('search', { default: '' });
const debouncedSearchQuery = refDebounced(searchQuery, 300);

const emit = defineEmits<DsMultiSelectEmits<T>>();

const selectElement = ref<HTMLSelectElement>();
const elementId = useId();

const wrapper = ref<HTMLDivElement>();

const controlWrapper = ref<HTMLDivElement>();

const isOpen = ref(false);

const truncatedChips = ref<Map<string | number, boolean>>(new Map());

const totalOptions = computed(() => {
    return props.options.filter(opt => !opt.disabled).length;
});

const hasSearchInput = computed(() => {
    return props.options.length > 5;
});

const hasSearchQuery = computed(() => {
    return !!searchQuery.value.length;
});

const visibleSelectedOptions = computed(() => {
    const selected = selectedOptions.value
        .map(item => props.options.find(opt => opt.id === item.id))
        .filter((opt): opt is T & DsMultiSelectOptionBase => opt !== undefined);
    if (selected.length <= props.maxVisibleChips) {
        return selected;
    }
    return selected.slice(0, props.maxVisibleChips);
});

const hiddenOptionsCount = computed(() => {
    const selectedCount = props.options.filter(opt => selectedIds.value.has(opt.id)).length;
    if (selectedCount <= props.maxVisibleChips) {
        return 0;
    }
    return selectedCount - props.maxVisibleChips;
});

const isGrouped = computed(() => !!props.groupLabel);

const groupedOptions = computed(() => {
    if (!isGrouped.value) return [{ label: undefined, items: props.options }];

    const groups = new Map<string | undefined, (T & DsMultiSelectOptionBase)[]>();
    props.options.forEach(option => {
        const groupKey = props.groupLabel && String(option[props.groupLabel as keyof (T & DsMultiSelectOptionBase)]);
        if (!groups.has(groupKey)) {
            groups.set(groupKey, []);
        }
        groups.get(groupKey)!.push(option);
    });

    return Array.from(groups.entries()).map(([label, items]) => ({
        label,
        items,
    }));
});

const filteredOptions = computed(() => {
    if (!props.options.length) return [];
    if (!searchQuery.value) return groupedOptions.value;

    const fuzzyRegex = createFuzzyRegex(debouncedSearchQuery.value);

    return groupedOptions.value
        .map(group => ({
            label: group.label,
            items: group.items.filter(item => fuzzyRegex.test(item.label)),
        }))
        .filter(group => !!group.items.length);
});

const selectedIds = computed(() => new Set(selectedOptions.value.map(item => item.id)));

const selectAllState = computed(() => {
    if (!totalOptions.value) return SelectAllState.Unchecked;

    const selectedCount = props.options.filter(opt => selectedIds.value.has(opt.id) && !opt.disabled).length;

    if (selectedCount === 0) return SelectAllState.Unchecked;
    if (selectedCount === totalOptions.value) return SelectAllState.Checked;
    return SelectAllState.Indeterminate;
});

const selectAllModel = computed({
    get: () => {
        return selectAllState.value !== SelectAllState.Unchecked;
    },
    set: (checked: boolean) => {
        const visibleOptionIds = new Set(filteredOptions.value.flatMap(group => group.items.map(item => item.id)));

        if (checked) {
            const optionsToAdd = props.options.filter(
                option => visibleOptionIds.has(option.id) && !selectedIds.value.has(option.id) && !option.disabled,
            );
            selectedOptions.value = [...selectedOptions.value, ...optionsToAdd];
        } else {
            selectedOptions.value = selectedOptions.value.filter(selectedItem => !visibleOptionIds.has(selectedItem.id));
        }
    },
});

const handleIndividualChange = (id: string | number, checked: boolean) => {
    const idString = id.toString();
    const option = props.options.find(opt => opt.id.toString() === idString);
    if (!option || option.disabled) return;
    if (checked) {
        if (!selectedIds.value.has(idString)) {
            selectedOptions.value = [...selectedOptions.value, option];
        }
    } else {
        selectedOptions.value = selectedOptions.value.filter(item => item.id !== id);
    }

    emit('toggleOption', { option, selected: checked });
};

const handleGroupSelectAll = (items: (T & DsMultiSelectOptionBase)[], checked: boolean) => {
    if (checked) {
        const newItems = items.filter(item => !selectedIds.value.has(item.id) && !item.disabled);
        selectedOptions.value = [...selectedOptions.value, ...newItems] as T[];
    } else {
        const itemIds = new Set(items.map(item => item.id));
        selectedOptions.value = selectedOptions.value.filter(item => !itemIds.has(item.id));
    }
};

const truncateChipLabel = (optionId: string | number) => (text: string) => {
    const maxLength = 35;
    const isTruncated = text.length > maxLength;

    truncatedChips.value.set(optionId, isTruncated);

    if (isTruncated) {
        return text.slice(0, 32) + '...';
    }
    return text;
};

onKeyDown('Enter', () => {
    if (!isOpen.value && document.activeElement === controlWrapper.value && !props.disabled) {
        isOpen.value = true;
    }
});

const hasError = computed(() => !!props.error);
const errorMessage = computed(() => {
    if (typeof props.error === 'string') {
        return props.error;
    }
    return '';
});

const hasWarning = computed(() => !hasError.value && !!props.warning);
const validationState = computed(() => {
    if (hasError.value) return 'error';
    if (hasWarning.value) return 'warning';
    return null;
});

const refreshInputValidity = (hasError: boolean) => {
    selectElement.value?.setCustomValidity(hasError ? errorMessage.value || 'Error' : '');
};

watch(hasError, refreshInputValidity);

const handleOpen = (event: boolean) => {
    if (!props.disabled) isOpen.value = event;
};

watch(isOpen, open => {
    if (open) {
        emit('open');
    } else {
        controlWrapper.value?.blur();
        emit('close');
    }
});

defineExpose({
    handleOpen,
});
</script>

<style scoped lang="scss">
.control-wrapper {
    @apply flex items-center bg-primary outline outline-1 outline-ds-primary-400 hover:bg-secondary;

    &:focus:not(.is-open):not(.is-disabled) {
        @apply shadow-outline-field outline-2 outline-ds-gray-800;
    }

    &.is-open:not(.is-disabled) {
        @apply shadow-outline-field outline-2 outline-ds-gray-800;
    }

    &.has-warning:not(.is-open) {
        @apply outline-2 outline-ds-yellow-400;
    }

    &.has-error:not(.is-open) {
        @apply outline-2 outline-ds-red-600;
    }

    &.is-disabled {
        @apply cursor-not-allowed bg-ds-gray-100 text-content-disabled;
    }
}

.ds-multiselect-size-md {
    .control-wrapper {
        @apply min-h-10 py-2;
    }

    .ds-multiselect-placeholder {
        @apply text-content-lg;
    }
}

.ds-multiselect-size-sm {
    .control-wrapper {
        @apply min-h-9 py-1.5;
    }

    .ds-multiselect-placeholder {
        @apply text-content-md;
    }
}
</style>
